import { ContextWithQuery, defaultDereferenceMessages, ExecutePipelineDetails, normalizeMessagesOrName, PipelineDetailsData } from "@me8eon/agent";
import { AiAndToolOptions, AiClients, AiToolsRepository, callAiAndAnyTools } from "@me8eon/aiclient";
import { ErrorsOr, flatMapLogK, LogAnd, mapLog } from "@me8eon/errors";
import { Message } from "@me8eon/messages";

export type LlmPipelineDetails = {
    type: "llm";
    model: string;
    toolSelectionModel?: string;
    prefix?: any;
    temperature?: number;
    tools?: string[];
};

export const executeLlmPipelineDetails = <Context extends ContextWithQuery>(
    aiClients: AiClients,
    toolRepo: AiToolsRepository
): ExecutePipelineDetails<Context, LlmPipelineDetails> =>
    async (p: LlmPipelineDetails, input: PipelineDetailsData<Context>): Promise<LogAnd<ErrorsOr<PipelineDetailsData<Context>>>> => {
        const { context, originalMessages, appendMessages = [], stream } = input;

        return flatMapLogK(
            defaultDereferenceMessages({ context }, normalizeMessagesOrName(p.prefix || [], "system")),
            async prefixMessages => {
                const queryScopedLanguageLock = `### PER-TURN LANGUAGE LOCK\nCurrent user message: "${context.query}"\nYou must write your final answer only in the same language as the current user message. Do not switch languages.`;
                const firstSystemIndex = prefixMessages.findIndex(msg => msg.role === "system" && typeof msg.content === "string");
                const effectivePrefixMessages = [...prefixMessages];
                if (firstSystemIndex >= 0) {
                    const firstSystemMessage = effectivePrefixMessages[firstSystemIndex];
                    effectivePrefixMessages[firstSystemIndex] = {
                        ...firstSystemMessage,
                        content: `${queryScopedLanguageLock}\n\n${firstSystemMessage.content}`,
                    };
                } else {
                    effectivePrefixMessages.unshift({
                        role: "system",
                        content: `You must answer only in the same language as the current user message: "${context.query}".`,
                    });
                }
                const messages: Message[] = [...effectivePrefixMessages, ...originalMessages, ...appendMessages];
                const aiClient = aiClients[p.model];

                if (!aiClient) {
                    throw new Error(`No AI client configured with name '${p.model}'. Legal names are: ${Object.keys(aiClients).sort()}`);
                }

                // Reinforced on every tool-loop call (not persisted to history) so long tool results can't dilute it.
                const languageReminder: Message = { role: "user", content: queryScopedLanguageLock };

                const options: AiAndToolOptions = {
                    legalTools: p.tools || [],
                    toolRepo,
                    temperature: p.temperature,
                    toolSelectionModel: p.toolSelectionModel,
                    languageReminder,
                    ...(stream?.sink ? { stream: { sink: stream.sink } } : {})
                };

                const response: LogAnd<ErrorsOr<Message[]>> = await callAiAndAnyTools(
                    aiClient,
                    toolRepo,
                    messages,
                    options
                );

                return mapLog(response, value => ({
                    ...input,
                    appendMessages: [...appendMessages, ...value]
                }));
            }
        );
    };